import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import { RootState } from 'store/configureStore';
import { TitleListEN, TitleListsCompliance } from 'store/ducks/globalTypes';
import { TitleKeyCompliance } from 'store/ducks/Management/types';
import { TITLE_KEYWORD_MAP } from 'constants/titlKeyword';
import auditFindingsWorkflow from 'constants/auditFindingsWorkflow';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import CardComponent from 'components/CardComponent';
import complianceEstAreas from 'constants/complianceEstAreas';
import riskAndControlAreas, {
  structureAndMaintainAreas,
  connectAndGovernAreas,
  assessAndTestAreas
} from 'constants/risksAndControlsAreas';
import inventoryAreas from 'constants/inventoryAreas';
import riskAndControlAllocationAreas from 'constants/riskAndControlAllocationAreas';
import i18n from 'constants/translation/i18n';
import { useTranslation } from 'react-i18next';
import * as Styled from 'components/Layouts/Layout.styled';

const { useToken } = theme;

const RISK_CONTROL_TYPE_MAP: Record<string, (t?: any) => any[]> = {
  structure_maintain: structureAndMaintainAreas,
  connect_and_govern: connectAndGovernAreas,
  assess_and_test: assessAndTestAreas,
  inventories: inventoryAreas,
  allocation: riskAndControlAllocationAreas
};

const MainLayout: React.FC = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selected } = useParams();

  const lang = useSelector((state: RootState) => state.global.lang);
  const [entriesToShow, setEntriesToShow] = useState<any[]>([]);
  const location = useLocation();

  const ROUTE_TO_TITLE_MAP: Record<string, string> = {
    'rules-to-evidence': 'Rules-to-Evidence Workflow',
    'audit-findings': 'Audit findings',
    'internal-controls': 'Business Process & Risk Control Hub'
  };
  const selectedArea = selected ? ROUTE_TO_TITLE_MAP[selected] || '' : '';

  const { scheme } = useSelector((state: RootState) => state.global);
  const pathname = location.pathname;
  const riskControlSegment = pathname.startsWith('/risk-controls/')
    ? pathname.split('/risk-controls/')[1]
    : undefined;

  const findTitleKeyFromMap = (translatedTitle: string): string | null => {
    const lowerTitle = translatedTitle.toLowerCase();

    if (TITLE_KEYWORD_MAP[lowerTitle]) {
      return TITLE_KEYWORD_MAP[lowerTitle];
    }
    for (const [keyword, titleKey] of Object.entries(TITLE_KEYWORD_MAP)) {
      if (lowerTitle.includes(keyword.toLowerCase())) {
        return titleKey;
      }
    }

    return null;
  };
  useEffect(() => {
    i18n.changeLanguage(lang);

    let entries: any[] = [];
    if (pathname === '/internal_controls') {
      entries = riskAndControlAreas(i18n.t);
      setEntriesToShow(entries);
      return;
    }
    if (riskControlSegment) {
      const areaFunction = RISK_CONTROL_TYPE_MAP[riskControlSegment];
      if (areaFunction) {
        entries = areaFunction(i18n.t);
      }
      setEntriesToShow(entries);
      return;
    }

    const titleKey = findTitleKeyFromMap(selectedArea);

    if (titleKey === 'AUDIT_FINDINGS') {
      entries = auditFindingsWorkflow(i18n.t);
    } else if (titleKey === 'COMP_EST') {
      entries = complianceEstAreas(i18n.t);
    } else if (titleKey === 'INTERNAL_CONTROLS') {
      entries = riskAndControlAreas(i18n.t);
    }

    setEntriesToShow(entries);
  }, [lang, pathname, riskControlSegment, selectedArea]);

  useEffect(() => {
    if (!selectedArea) return;

    const titleKey = findTitleKeyFromMap(selectedArea);
    if (!titleKey) return;

    const redirects: Record<string, string> = {
      LIBRARY: '/library',
      USER_ACCESS: '/user_access',
      WHO_AM_I: '/who_am_i',
      DEPENDENCY_TREE: '/dependencies'
    };

    const target = redirects[titleKey];

    if (target && location.pathname !== target) {
      navigate(target);
    }
  }, [selectedArea, navigate, location.pathname]);

  const getTitleKeyFromTitle = (
    selectedTitle: string,
    titleEnum: Record<TitleKeyCompliance, string>
  ): TitleKeyCompliance => {
    return (
      (Object.keys(titleEnum) as TitleKeyCompliance[]).find(
        (key) => titleEnum[key] === selectedTitle
      ) ?? 'COMP_EST'
    );
  };

  const TitleKeyComplianceLayout = getTitleKeyFromTitle(
    selectedArea,
    TitleListsCompliance['en']
  );

  const titles = TitleListsCompliance[lang] || TitleListEN;

  const getPageTitle = () => {
    if (pathname === '/internal_controls') {
      return (
        i18n.t('INTERNAL_CONTROLS') || 'Business Process & Risk Control Hub'
      );
    }

    switch (riskControlSegment) {
      case 'structure_maintain':
        return i18n.t('STRUCTURE_AND_MAINTAIN');

      case 'connect_and_govern':
        return i18n.t('CONNECT_AND_GOVERN');

      case 'assess_and_test':
        return i18n.t('ASSESS_AND_TEST');

      case 'testings':
        return i18n.t('TESTING');

      case 'reportings':
        return i18n.t('REPORTING');

      case 'allocation':
        return i18n.t('ALLOCATION_MAIN');

      case 'inventories':
        return i18n.t('INVENTORIES');

      default:
        break;
    }

    if (selectedArea === 'Audit findings') {
      return i18n.t('AUDIT_FINDING');
    }

    return titles[TitleKeyComplianceLayout];
  };

  if (selectedArea === 'Library') return null;

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={getPageTitle()}
        goBackButtons={
          pathname === '/internal_controls' ? (
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          ) : riskControlSegment ? (
            <>
              <GoBackButton to='/internal_controls' text={t('GO_BACK')} />
              <GoBackButton
                to='/working_area'
                text={t('GO_TO_WORKING_AREAS')}
              />
            </>
          ) : selectedArea === 'Audit findings' ? (
            <>
              <GoBackButton
                to='/workingAreaDetails/rules-to-evidence'
                text={t('GO_BACK')}
              />
              <GoBackButton
                to='/working_area'
                text={t('GO_TO_WORKING_AREAS')}
              />
            </>
          ) : (
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          )
        }
      />
      <Styled.HorizontalLayout theme={scheme}>
        <Styled.Container>
          <Styled.GridContainer token={token}>
            {entriesToShow.map((item: any) => (
              <Styled.RelativeContainer key={item.area}>
                <CardComponent
                  area={item.title}
                  infoText={item.infoText}
                  href={item.href}
                  textOnTop=''
                />
              </Styled.RelativeContainer>
            ))}
          </Styled.GridContainer>
        </Styled.Container>
      </Styled.HorizontalLayout>
    </Styled.StyledLayout>
  );
};

export default MainLayout;
